import React, { forwardRef, Ref, useRef, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextInput as RNTextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';

import generateID from '../../utils/generate-id';
import Button from '../atoms/Button';
import Dialog from '../atoms/Dialog';
import Drawer from '../atoms/Drawer';
import Icon from '../atoms/Icon';
import Select from '../atoms/Select';
import TextInput, { TextInputProps } from '../atoms/TextInput';

type Option = {
  label: string;
  value: string;
};

type Props = Pick<
  TextInputProps,
  | 'accessibilityLabel'
  | 'editable'
  | 'id'
  | 'label'
  | 'placeholder'
  | 'onFocus'
  | 'onBlur'
  | 'value'
  | 'message'
  | 'state'
  | 'style'
> & {
  options: Option[];
  /**
   * Callback function when the select button is pressed
   */
  onSelect?: (value: string) => void;
  selectButtonText: string;
  /**
   * Show Picker inside a Dialog rather than in Drawer
   */
  showAsDialog?: boolean;
  /**
   * Custom style for the Dialog
   */
  dialogStyle?: StyleProp<ViewStyle>;
  selectorAccessibilityLabel?: string;
  selectButtonAccessibilityLabel?: string;
};

const OPTION_HEIGHT = 32;

const Picker = forwardRef(
  (props: Props, ref: Ref<TouchableWithoutFeedback>) => {
    const isEditable = props.editable === undefined || !!props.editable;
    const [isOpen, setIsOpen] = useState(false);

    const [selectedValue, setSelectedValue] = useState('');

    const textInputRef = useRef<RNTextInput | null>(null);

    const onPickerOpen = () => {
      setSelectedValue(props.value);
    };

    const closePicker = () => {
      textInputRef.current?.focus();
      setIsOpen(false);
    };

    const content = (
      <>
        <Select
          value={selectedValue}
          onSelect={(value) => setSelectedValue(value)}
          options={props.options}
          id={props.id && `${props.id}-select`}
        />
        <View style={style.spacer} />
        <Button
          accessibilityLabel={props.selectButtonAccessibilityLabel}
          title={props.selectButtonText}
          onPress={() => {
            props.onSelect?.(selectedValue);
            closePicker();
          }}
          disabled={!selectedValue}
          id={props.id && `${props.id}-select-button`}
        />
      </>
    );

    return (
      <>
        <TouchableWithoutFeedback
          ref={ref}
          disabled={!isEditable}
          onPress={() => setIsOpen(true)}
          /** Generate HTML and Test IDs  */
          {...generateID(props.id)}
        >
          <View style={style.root}>
            <TextInput
              {...props}
              onBlur={(e) => !isOpen && props.onBlur?.(e)}
              editable={props.editable}
              value={props.value}
              id={props.id && `${props.id}-field`}
            />
            <TouchableOpacity
              disabled={!isEditable}
              onPress={() => setIsOpen(true)}
              style={style.openPickerIcon}
              /** Generate HTML and Test IDs  */
              {...generateID(props.id && `${props.id}-open-button`)}
              /** Platform-specific props */
              {...Platform.select({
                web: {
                  accessibilityRole: 'button',
                },
              })}
            >
              <Icon name="caret-down" size="24" />
            </TouchableOpacity>
          </View>
        </TouchableWithoutFeedback>
        {!props.showAsDialog && (
          <Drawer
            open={isOpen}
            onOpen={onPickerOpen}
            onBackdropPress={closePicker}
            anchor="bottom"
            style={style.drawer}
            accessibilityLabel={props.selectorAccessibilityLabel}
            id={props.id && `${props.id}-drawer`}
          >
            <View style={style.drawerControls}>
              <TouchableOpacity
                onPress={closePicker}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-drawer-close-button`)}
                /** Platform-specific props */
                {...Platform.select({
                  web: {
                    accessibilityRole: 'button',
                  },
                })}
              >
                <Icon name="caret-down" size="24" />
              </TouchableOpacity>
            </View>
            <View style={style.spacer} />
            {content}
          </Drawer>
        )}
        {props.showAsDialog && (
          <Dialog
            open={isOpen}
            onOpen={onPickerOpen}
            onBackdropPress={closePicker}
            onCloseButtonPress={closePicker}
            title={props.label || ''}
            style={props.dialogStyle}
            accessibilityLabel={props.selectorAccessibilityLabel}
            id={props.id && `${props.id}-dialog`}
          >
            {content}
          </Dialog>
        )}
      </>
    );
  }
);

const style = StyleSheet.create({
  root: {
    position: 'relative',
  },
  openPickerIcon: {
    position: 'absolute',
    top: 24,
    right: 8,
  },
  drawer: {
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 8,
  },
  spacer: {
    marginBottom: 24,
  },
  drawerControls: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  textInput: {
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  flatList: {
    height: 204,
    backgroundColor: 'transparent',
  },
  flatListContent: {
    marginVertical: 88,
  },
  option: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    height: OPTION_HEIGHT,
    textAlign: 'center',
    borderRadius: 4,
  },
  optionSelected: {
    backgroundColor: '#EEF0F2',
  },
});

export default Picker;
